import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Minus, Plus, Trash2, ArrowLeft, LogOut, Store, Bike, Printer, ClipboardCheck, Pencil, Check } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';
import Modal from '../components/Modal';
import IconoGrupo, { ICONOS_GRUPO } from '../components/IconoGrupo';
import BotonAyuda from '../components/BotonAyuda';
import AutocompleteProducto from '../components/AutocompleteProducto';

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const TIPO_FORMA_LABEL = {
  efectivo_bs: 'Efectivo Bs', efectivo_usd: 'Efectivo $', pos: 'POS',
  transferencia: 'Transferencia', pago_movil: 'Pago móvil', delivery: 'Delivery', otro: 'Otro',
  cuenta_por_cobrar: 'Cuenta por Cobrar'
};

// Denominaciones para el arqueo por billete (cierre de turno).
const DENOM_BS = [100, 50, 20, 10, 5, 1];
const DENOM_USD = [100, 50, 20, 10, 5, 1];

// Árbol de grupos: se navega un nivel a la vez por `padre_id`.
function construirArbol(grupos) {
  const hijosDe = {};
  grupos.forEach((g) => {
    const k = g.padre_id || 0;
    (hijosDe[k] = hijosDe[k] || []).push(g);
  });
  return hijosDe;
}

// Resiliencia offline (pendiente #91, paso 6): se cachea el catálogo y el
// estado del turno en localStorage para seguir vendiendo si se cae la
// conexión, y las ventas que no salen se encolan y se reintentan al
// reconectar. NO es un PWA completo: si la app no está cargada y no hay
// red, no abre.
const lsKey = (empresaId, sufijo) => `caja_${sufijo}_${empresaId}`;
function lsLeer(k) {
  try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; }
}
function lsGuardar(k, v) {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* cuota llena: se ignora */ }
}
// axios: si hay `response` el servidor contestó (ej. 400 de validación) y NO
// se debe encolar; si no, es problema de red.
const esErrorDeRed = (err) => !err?.response;

// Extras: el grupo raíz "Extras" no se muestra como tarjeta; sus productos
// se ofrecen al agregar una pizza (grupo raíz "Pizzas", filtrados por el
// mismo tamaño: Grande / Mediana / Pequeña) o cualquier producto marcado con
// `permite_extras` (ej. un combo), sin filtro de tamaño en ese caso.
const norm = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
function tamano(nombre) {
  const n = norm(nombre);
  if (/\bgrande\b/.test(n)) return 'grande';
  if (/\bbandeja\b/.test(n) && /\bnapolitana\b/.test(n)) return 'grande'; // lleva extras grandes
  if (/\bmedian[oa]s?\b/.test(n)) return 'mediana';
  if (/\bpequen[oa]s?\b/.test(n)) return 'pequena';
  if (/\bmini\b/.test(n)) return 'pequena'; // Mini-Pizza lleva extras pequeños
  return null;
}
function idsBajoRaiz(grupos, nombreRaiz) {
  const raiz = grupos.find((g) => !g.padre_id && norm(g.nombre) === nombreRaiz);
  if (!raiz) return new Set();
  const ids = new Set([raiz.id]);
  let crecio = true;
  while (crecio) {
    crecio = false;
    for (const g of grupos) {
      if (g.padre_id && ids.has(g.padre_id) && !ids.has(g.id)) { ids.add(g.id); crecio = true; }
    }
  }
  return ids;
}

export default function CajaVenta() {
  const { empresaActiva, empresaActivaNombre, rolActual, usuario } = useAuth();
  // "Editar pantalla": gerente/admin asignan el ícono de cada tarjeta de grupo.
  const puedeEditarPantalla = ['gerente', 'admin'].includes(rolActual);
  // Carga histórica (meses atrasados sin registrar): solo contador/gerente/
  // admin pueden abrir un turno con fecha pasada (mismo criterio que el servidor).
  const puedeAbrirFechaPasada = ['contador', 'gerente', 'admin'].includes(rolActual);
  const [editandoPantalla, setEditandoPantalla] = useState(false);
  const [grupoIcono, setGrupoIcono] = useState(null); // grupo cuyo ícono se está eligiendo

  const [estado, setEstado] = useState(null);
  // El operador cierra el turno que abrió con su usuario; contador o más,
  // cualquiera (mismo criterio que el servidor en cerrarCierre).
  const puedeCerrarTurno = ['contador', 'gerente', 'admin'].includes(rolActual) ||
    (rolActual === 'operador' && Number(estado?.cierre?.abierto_por_usuario) === Number(usuario?.id));
  const [catalogo, setCatalogo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  // Login de cajero
  const [pin, setPin] = useState('');
  const [cajeros, setCajeros] = useState([]);

  // Apertura de turno
  const [turno, setTurno] = useState('unico');
  const [fechaApertura, setFechaApertura] = useState(hoyCaracas());
  const [fondoInicial, setFondoInicial] = useState('');
  const [fondoUsd, setFondoUsd] = useState('');
  const [fondoDigital, setFondoDigital] = useState('');

  // POS
  const [grupoSel, setGrupoSel] = useState(0);
  const [ticket, setTicket] = useState([]); // [{key, producto_id, grupo_id, nombre, precio_usd, cantidad, padre?}]
  const [pizzaActiva, setPizzaActiva] = useState(null); // key de la línea de pizza a la que se le agregan extras
  const [canal, setCanal] = useState('mostrador');
  const [plataformaId, setPlataformaId] = useState(''); // '' = sin definir
  const [eligiendoPlataforma, setEligiendoPlataforma] = useState(false);

  // Cobro
  const [cobrando, setCobrando] = useState(false);
  const [pagos, setPagos] = useState({});
  const [conIva, setConIva] = useState(true); // false = ticket solo por el monto base
  const [vueltoFormaId, setVueltoFormaId] = useState(null); // forma en que se devuelve el excedente (null = todo propina)
  const [vueltoMonto, setVueltoMonto] = useState(''); // '' = todo el excedente
  const [formasElegidas, setFormasElegidas] = useState([]); // ids de formas de pago en el orden en que se tocaron
  const [aviso, setAviso] = useState(null);
  const [errorCobro, setErrorCobro] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Cuenta por Cobrar (#91): el cliente al que se le carga el ticket se
  // busca o se crea en el momento, no se preconfigura por fuera de Caja.
  const [clientesCxc, setClientesCxc] = useState([]);
  const [clienteCxcId, setClienteCxcId] = useState(null);
  const [nuevoClienteCxc, setNuevoClienteCxc] = useState(null); // { nombre, telefono } mientras el modal está abierto
  const [guardandoClienteCxc, setGuardandoClienteCxc] = useState(false);
  const [errorClienteCxc, setErrorClienteCxc] = useState('');

  // Cierre / arqueo
  const [mostrarCierre, setMostrarCierre] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [arqueoBs, setArqueoBs] = useState('');
  const [arqueoUsd, setArqueoUsd] = useState('');
  const [arqueoDigital, setArqueoDigital] = useState('');
  const [obsCierre, setObsCierre] = useState('');
  const [motivoTardio, setMotivoTardio] = useState('');
  const [encargado, setEncargado] = useState('');
  const [revisado, setRevisado] = useState('');
  const [billetesBs, setBilletesBs] = useState({}); // { denominacion: cantidadStr }
  const [billetesUsd, setBilletesUsd] = useState({});
  const [posInfo, setPosInfo] = useState({}); // { forma_pago_id: { banco, lote } }
  const [errCierre, setErrCierre] = useState('');
  const [procesandoCierre, setProcesandoCierre] = useState(false);

  // Offline
  const [offline, setOffline] = useState(false);
  const [cola, setCola] = useState([]);
  const [rechazados, setRechazados] = useState([]);

  const K_ESTADO = lsKey(empresaActiva, 'estado');
  const K_CATALOGO = lsKey(empresaActiva, 'catalogo');
  const K_COLA = lsKey(empresaActiva, 'cola');
  // Tickets de la cola que el servidor rechazó: se guardan aparte (nunca se
  // borran solos) para poder descargarlos y recargarlos a mano.
  const K_RECHAZADOS = lsKey(empresaActiva, 'rechazados');

  async function cargarEstado() {
    const { data } = await api.get('/caja/estado');
    setEstado(data);
    lsGuardar(K_ESTADO, data);
    return data;
  }

  async function cargarTodo() {
    setCargando(true);
    setError('');
    try {
      const est = await cargarEstado();
      if (est.cajero && est.cierre && est.tasa_bcv != null) {
        const { data } = await api.get('/caja/catalogo');
        setCatalogo(data);
        lsGuardar(K_CATALOGO, data);
      } else if (!est.cajero) {
        const { data } = await api.get('/caja/catalogos/cajeros');
        setCajeros(data.filter((c) => c.activo));
      }
      setOffline(false);
    } catch (err) {
      if (esErrorDeRed(err)) {
        // Sin red: intentar seguir con lo cacheado.
        const estCache = lsLeer(K_ESTADO);
        const catCache = lsLeer(K_CATALOGO);
        if (estCache && catCache && estCache.cajero && estCache.cierre) {
          setEstado(estCache);
          setCatalogo(catCache);
          setOffline(true);
        } else {
          setError('Sin conexión y sin datos guardados para trabajar offline.');
        }
      } else {
        setError(err.response?.data?.error || 'No se pudo cargar la caja.');
      }
    } finally {
      setCargando(false);
    }
  }

  async function flushCola() {
    const pendientes = lsLeer(K_COLA) || [];
    if (pendientes.length === 0) return;
    let procesadas = 0; // enviadas OK o apartadas por rechazo del servidor
    const nuevosRechazados = [];
    for (let i = 0; i < pendientes.length; i++) {
      try {
        await api.post('/caja/venta', pendientes[i].payload);
        procesadas++;
      } catch (err) {
        // Sin red o sin sesión de cajero: conservar el resto para reintentar.
        if (esErrorDeRed(err) || err.response?.status === 401) break;
        nuevosRechazados.push({ ...pendientes[i], motivo: err.response?.data?.error || 'error de validación' });
        procesadas++; // no se puede reenviar: pasa a rechazados
      }
    }
    if (nuevosRechazados.length) {
      const todos = [...(lsLeer(K_RECHAZADOS) || []), ...nuevosRechazados];
      lsGuardar(K_RECHAZADOS, todos);
      setRechazados(todos);
      setError(`${nuevosRechazados.length} ticket(s) en cola fueron rechazados: ${nuevosRechazados[0].motivo}. Quedaron guardados en esta caja; descárgalos desde el aviso de arriba.`);
    }
    const quedan = pendientes.slice(procesadas);
    lsGuardar(K_COLA, quedan);
    setCola(quedan);
    if (procesadas > 0 && quedan.length === 0) cargarTodo();
  }

  useEffect(() => {
    if (empresaActiva) {
      // El token del cajero es por empresa: solo se descarta al cambiar de empresa,
      // no al recargar la pantalla.
      if (localStorage.getItem('cajaCajeroEmpresa') !== String(empresaActiva)) {
        localStorage.removeItem('cajaCajeroToken');
      }
      // Borrador: la factura en curso sobrevive a salir de la pantalla o recargar.
      const borrador = lsLeer(lsKey(empresaActiva, 'borrador'));
      setTicket(borrador?.ticket || []);
      setCanal(borrador?.canal || 'mostrador');
      setPlataformaId(borrador?.plataformaId || '');
      borradorEmpresa.current = empresaActiva;
      setCatalogo(null);
      setCola(lsLeer(lsKey(empresaActiva, 'cola')) || []);
      setRechazados(lsLeer(lsKey(empresaActiva, 'rechazados')) || []);
      cargarTodo().then(flushCola);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  // Guarda el borrador en cada cambio (solo después de haber cargado el de esta empresa).
  const borradorEmpresa = useRef(null);
  useEffect(() => {
    if (!empresaActiva || borradorEmpresa.current !== empresaActiva) return;
    lsGuardar(lsKey(empresaActiva, 'borrador'), { ticket, canal, plataformaId });
  }, [empresaActiva, ticket, canal, plataformaId]);

  // Si la factura queda vacía mientras se cobra, se vuelve a productos.
  useEffect(() => {
    if (cobrando && ticket.length === 0) setCobrando(false);
  }, [cobrando, ticket.length]);

  useEffect(() => {
    function alReconectar() { cargarTodo().then(flushCola); }
    function alDesconectar() { setOffline(true); }
    window.addEventListener('online', alReconectar);
    window.addEventListener('offline', alDesconectar);
    return () => {
      window.removeEventListener('online', alReconectar);
      window.removeEventListener('offline', alDesconectar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  async function iniciarSesionCajero(e, pinIngresado = pin) {
    e?.preventDefault();
    if (pinIngresado.length !== 4) return;
    setError('');
    try {
      const { data } = await api.post('/caja/cajero/login', { pin: pinIngresado });
      localStorage.setItem('cajaCajeroToken', data.token);
      localStorage.setItem('cajaCajeroEmpresa', String(empresaActiva));
      setPin('');
      await cargarTodo();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo iniciar sesión de cajero.');
      setPin(''); // PIN incorrecto: limpiar para reintentar de una
    }
  }

  function cerrarSesionCajero() {
    localStorage.removeItem('cajaCajeroToken');
    setTicket([]);
    setCatalogo(null);
    cargarTodo();
  }

  async function abrirTurno(e) {
    e.preventDefault();
    setError('');
    try {
      await api.post('/caja/cierre', {
        turno,
        fecha: puedeAbrirFechaPasada ? fechaApertura : undefined,
        fondo_inicial_bs: Number(fondoInicial) || 0,
        fondo_inicial_usd: Number(fondoUsd) || 0,
        fondo_digital_bs: Number(fondoDigital) || 0
      });
      await cargarTodo();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo abrir el turno.');
    }
  }

  // ---------- POS ----------
  async function asignarIcono(grupo, icono) {
    const previo = catalogo;
    const nuevo = { ...catalogo, grupos: catalogo.grupos.map((g) => (g.id === grupo.id ? { ...g, icono: icono || null } : g)) };
    setCatalogo(nuevo);
    lsGuardar(K_CATALOGO, nuevo);
    setGrupoIcono(null);
    try {
      await api.put(`/caja/catalogos/grupos-icono/${grupo.id}`, { icono });
    } catch (err) {
      setCatalogo(previo);
      lsGuardar(K_CATALOGO, previo);
      alert(err.response?.data?.error || 'No se pudo guardar el ícono');
    }
  }

  const arbol = useMemo(() => (catalogo ? construirArbol(catalogo.grupos) : {}), [catalogo]);
  const extrasIds = useMemo(() => (catalogo ? idsBajoRaiz(catalogo.grupos, 'extras') : new Set()), [catalogo]);
  const pizzasIds = useMemo(() => (catalogo ? idsBajoRaiz(catalogo.grupos, 'pizzas') : new Set()), [catalogo]);
  const gruposNivel = (arbol[grupoSel] || []).filter((g) => !extrasIds.has(g.id));
  const grupoActual = useMemo(
    () => (catalogo && grupoSel ? catalogo.grupos.find((g) => g.id === grupoSel) : null),
    [catalogo, grupoSel]
  );
  const productosNivel = useMemo(
    () => (catalogo && grupoSel ? catalogo.productos.filter((p) => p.grupo_id === grupoSel) : []),
    [catalogo, grupoSel]
  );

  const pizzaSel = ticket.find((l) => l.key === pizzaActiva) || null;
  const extrasDisponibles = useMemo(() => {
    if (!catalogo || !pizzaSel) return [];
    const t = tamano(pizzaSel.nombre);
    return catalogo.productos
      .filter((p) => extrasIds.has(p.grupo_id))
      .filter((p) => { const te = tamano(p.nombre); return !t || !te || te === t; });
  }, [catalogo, pizzaSel, extrasIds]);
  const extrasAbiertos = !!pizzaSel && extrasDisponibles.length > 0;

  const tasa = estado?.cierre?.tasa_bcv ? Number(estado.cierre.tasa_bcv) : (catalogo?.tasa_bcv || 0);
  const turnoVencido = !!estado?.cierre?.vencido;
  const ivaPct = estado?.cierre?.iva_pct != null ? Number(estado.cierre.iva_pct) : (catalogo?.iva_pct ?? 16);

  const baseUsd = round2(ticket.reduce((s, l) => s + round2(l.cantidad * l.precio_usd), 0));
  const ivaUsd = conIva ? round2(baseUsd * ivaPct / 100) : 0;
  const totalUsd = round2(baseUsd + ivaUsd);

  function agregar(prod) {
    if (prod.precio_usd == null) {
      setError(`"${prod.nombre}" no tiene precio de venta cargado.`);
      return;
    }
    setError('');
    // Cada pizza con tamaño va en su propia línea para poder llevar sus extras.
    // Sin tamaño reconocible (ej. Bandeja Napolitana) no lleva extras: va como producto normal.
    // `permite_extras` abre el mismo flujo para productos marcados caso por
    // caso (ej. un combo), sin depender de la heurística de pizza/tamaño.
    if ((pizzasIds.has(prod.grupo_id) && tamano(prod.nombre)) || prod.permite_extras) {
      const key = `z${prod.id}-${Date.now()}`;
      setTicket((prev) => [...prev, { key, producto_id: prod.id, grupo_id: prod.grupo_id, nombre: prod.nombre, precio_usd: Number(prod.precio_usd), cantidad: 1, tieneExtras: true }]);
      setPizzaActiva(key);
      return;
    }
    setTicket((prev) => {
      const i = prev.findIndex((l) => l.producto_id === prod.id && !l.padre);
      if (i >= 0) {
        const copia = [...prev];
        copia[i] = { ...copia[i], cantidad: copia[i].cantidad + 1 };
        return copia;
      }
      return [...prev, { key: `p${prod.id}`, producto_id: prod.id, grupo_id: prod.grupo_id, nombre: prod.nombre, precio_usd: Number(prod.precio_usd), cantidad: 1 }];
    });
  }

  // Extra: se agrega / quita (toggle) de la pizza activa; su cantidad sigue a la de la pizza.
  function alternarExtra(extra) {
    const pizza = ticket.find((l) => l.key === pizzaActiva);
    if (!pizza || extra.precio_usd == null) return;
    setTicket((prev) => {
      const existe = prev.some((l) => l.padre === pizza.key && l.producto_id === extra.id);
      if (existe) return prev.filter((l) => !(l.padre === pizza.key && l.producto_id === extra.id));
      const nueva = { key: `x${extra.id}-${pizza.key}`, padre: pizza.key, producto_id: extra.id, grupo_id: extra.grupo_id, nombre: extra.nombre, precio_usd: Number(extra.precio_usd), cantidad: pizza.cantidad };
      // Insertar después de la pizza y sus extras ya agregados.
      let pos = prev.findIndex((l) => l.key === pizza.key) + 1;
      while (pos < prev.length && prev[pos].padre === pizza.key) pos += 1;
      return [...prev.slice(0, pos), nueva, ...prev.slice(pos)];
    });
  }

  // "Listo" / "Sin extra": cierra los extras y vuelve al menú principal.
  function terminarExtras() {
    setPizzaActiva(null);
    setGrupoSel(0);
  }

  function cambiarCantidad(key, delta) {
    setTicket((prev) => {
      const linea = prev.find((l) => l.key === key);
      if (!linea) return prev;
      const cant = linea.cantidad + delta;
      if (cant <= 0) return prev.filter((l) => l.key !== key && l.padre !== key);
      return prev.map((l) => (l.key === key || l.padre === key ? { ...l, cantidad: cant } : l));
    });
  }
  function quitar(key) {
    setTicket((prev) => prev.filter((l) => l.key !== key && l.padre !== key));
    if (pizzaActiva === key) setPizzaActiva(null);
  }
  function nuevoTicket() {
    setTicket([]);
    setConIva(true);
    setPizzaActiva(null);
    setCanal('mostrador');
    setPlataformaId('');
  }

  // Ítem libre: línea a mano sin producto del catálogo (descripción + precio $).
  const [itemLibre, setItemLibre] = useState(false);
  const [ilDesc, setIlDesc] = useState('');
  const [ilPrecio, setIlPrecio] = useState('');
  const [ilCant, setIlCant] = useState('1');
  function abrirItemLibre() {
    setIlDesc(''); setIlPrecio(''); setIlCant('1'); setItemLibre(true);
  }
  function agregarItemLibre(e) {
    e.preventDefault();
    const desc = ilDesc.trim();
    const precio = Number(ilPrecio);
    const cant = Number(ilCant);
    if (!desc || !(precio > 0) || !(cant > 0)) return; // ítem libre siempre positivo
    setTicket((prev) => [...prev, {
      key: `m${Date.now()}`, producto_id: null, grupo_id: null,
      nombre: desc, descripcion: desc, precio_usd: precio, cantidad: cant
    }]);
    setItemLibre(false);
  }

  // ---------- Cobro ----------
  const aCobrarUsd = totalUsd;
  const formasPago = catalogo?.formas_pago || [];
  const tendidoUsd = round2(
    formasPago.reduce((s, f) => {
      const monto = Number(pagos[f.id]) || 0;
      if (!monto) return s;
      return s + (f.moneda === 'USD' ? monto : (tasa ? monto / tasa : 0));
    }, 0)
  );
  const faltaUsd = round2(Math.max(0, aCobrarUsd - tendidoUsd));
  // Lo pagado de más queda como propina, salvo la parte que se devuelve como vuelto.
  const propinaUsd = round2(Math.max(0, tendidoUsd - aCobrarUsd));
  const formaCxc = formasPago.find((f) => f.tipo === 'cuenta_por_cobrar') || null;
  const usaCxc = !!(formaCxc && formasElegidas.includes(formaCxc.id));
  const formasVuelto = formasPago.filter((f) => ['efectivo_bs', 'efectivo_usd', 'pago_movil'].includes(f.tipo));
  const formaVuelto = propinaUsd > 0.005 ? formasPago.find((f) => f.id === vueltoFormaId) || null : null;
  const excedenteEnForma = formaVuelto ? (formaVuelto.moneda === 'USD' ? propinaUsd : round2(propinaUsd * tasa)) : 0;
  const vueltoMontoEf = formaVuelto
    ? Math.min(vueltoMonto !== '' ? (Number(vueltoMonto) || 0) : excedenteEnForma, excedenteEnForma)
    : 0;
  const vueltoUsdEf = formaVuelto ? (formaVuelto.moneda === 'USD' ? vueltoMontoEf : (tasa ? vueltoMontoEf / tasa : 0)) : 0;
  const propinaFinalUsd = round2(Math.max(0, propinaUsd - vueltoUsdEf));

  function abrirCobro() {
    setPagos({});
    setVueltoFormaId(null);
    setVueltoMonto('');
    setFormasElegidas([]);
    setErrorCobro('');
    setClienteCxcId(null);
    setErrorClienteCxc('');
    setCobrando(true);
    if (!offline && clientesCxc.length === 0) {
      api.get('/clientes').then(({ data }) => setClientesCxc(data.map((c) => ({ id: c.id, nombre: c.nombre }))))
        .catch(() => { /* sin red: se sigue pudiendo cobrar con otra forma de pago */ });
    }
  }
  // La Cuenta por Cobrar no se combina con otra forma en el mismo ticket
  // (decisión del usuario): elegirla reemplaza cualquier otra ya tocada, y
  // tocar otra la saca a ella.
  function elegirForma(forma) {
    if (forma.tipo === 'cuenta_por_cobrar') {
      setFormasElegidas([forma.id]);
      setPagos({ [forma.id]: String(forma.moneda === 'USD' ? aCobrarUsd : round2(aCobrarUsd * tasa)) });
      return;
    }
    if (usaCxc) { setClienteCxcId(null); setErrorClienteCxc(''); }
    setFormasElegidas((prev) => {
      const sinCxc = prev.filter((id) => id !== formaCxc?.id);
      return sinCxc.includes(forma.id) ? sinCxc : [...sinCxc, forma.id];
    });
    if (usaCxc) setPagos((prev) => { const c = { ...prev }; delete c[formaCxc.id]; return c; });
  }
  function quitarForma(formaId) {
    setFormasElegidas((prev) => prev.filter((id) => id !== formaId));
    setPagos((prev) => { const c = { ...prev }; delete c[formaId]; return c; });
    if (formaCxc && formaId === formaCxc.id) { setClienteCxcId(null); setErrorClienteCxc(''); }
  }
  function abrirNuevoClienteCxc(texto) {
    setErrorClienteCxc('');
    setNuevoClienteCxc({ nombre: texto, telefono: '' });
  }
  async function crearClienteCxc() {
    const nombre = (nuevoClienteCxc?.nombre || '').trim();
    if (!nombre) return;
    setGuardandoClienteCxc(true);
    setErrorClienteCxc('');
    try {
      const { data } = await api.post('/clientes', { nombre, telefono: (nuevoClienteCxc.telefono || '').trim() || undefined });
      setClientesCxc((prev) => [...prev, { id: data.id, nombre }].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      setClienteCxcId(data.id);
      setNuevoClienteCxc(null);
    } catch (err) {
      setErrorClienteCxc(err.response?.data?.error || 'No se pudo crear el cliente.');
    } finally {
      setGuardandoClienteCxc(false);
    }
  }
  function completarConFalta(forma) {
    const montoForma = forma.moneda === 'USD' ? faltaUsd : round2(faltaUsd * tasa);
    setPagos((prev) => ({ ...prev, [forma.id]: String(montoForma) }));
  }

  async function confirmarCobro() {
    setErrorCobro('');
    if (faltaUsd > 0.01) { setErrorCobro('El pago no cubre el total.'); return; }
    if (usaCxc && !clienteCxcId) { setErrorCobro('Seleccioná a qué cliente se le carga la Cuenta por Cobrar.'); return; }
    const pagosPayload = formasPago
      .filter((f) => Number(pagos[f.id]) > 0)
      .map((f) => ({ forma_pago_id: f.id, moneda: f.moneda, monto: Number(pagos[f.id]) }));
    if (pagosPayload.length === 0) { setErrorCobro('Agregá al menos un pago.'); return; }

    const payload = {
      canal,
      plataforma_id: canal === 'delivery' && plataformaId ? Number(plataformaId) : null,
      cliente_id: usaCxc ? clienteCxcId : null,
      lineas: ticket.map((l) => (
        l.producto_id
          ? {
              producto_id: l.producto_id, grupo_id: l.grupo_id, cantidad: l.cantidad,
              ...(l.padre ? { padre_index: ticket.findIndex((x) => x.key === l.padre) } : {})
            }
          : { producto_id: null, descripcion: l.descripcion, precio_usd: l.precio_usd, cantidad: l.cantidad }
      )),
      pagos: pagosPayload,
      con_iva: conIva,
      vueltos: formaVuelto && vueltoMontoEf > 0 ? [{ forma_pago_id: formaVuelto.id, monto: round2(vueltoMontoEf) }] : [],
      excedente_propina: true // lo pagado de más es propina, descontando el vuelto
    };

    setGuardando(true);
    try {
      const { data } = await api.post('/caja/venta', payload);
      setCobrando(false);
      nuevoTicket();
      setAviso({ numero: data.numero, vuelto_bs: data.vuelto_bs, total_bs: data.total_bs, propina_bs: data.propina_bs, factura_cxc_numero: data.factura_cxc_numero });
      setTimeout(() => setAviso(null), 6000);
    } catch (err) {
      if (esErrorDeRed(err)) {
        // Sin conexión: se encola y se reintenta al reconectar.
        const nuevaCola = [...(lsLeer(K_COLA) || []), { ts: Date.now(), payload }];
        lsGuardar(K_COLA, nuevaCola);
        setCola(nuevaCola);
        setOffline(true);
        setCobrando(false);
        nuevoTicket();
        setAviso({ numero: null, vuelto_bs: 0, total_bs: totalUsd * tasa, encolado: true });
        setTimeout(() => setAviso(null), 6000);
      } else {
        setErrorCobro(err.response?.data?.error || 'No se pudo registrar la venta.');
      }
    } finally {
      setGuardando(false);
    }
  }

  // ---------- Cierre / arqueo ----------
  function hidratarCierre(data) {
    setResumen(data);
    setArqueoBs(data.cierre.efectivo_bs_contado != null ? String(data.cierre.efectivo_bs_contado) : '');
    setArqueoUsd(data.cierre.efectivo_usd_contado != null ? String(data.cierre.efectivo_usd_contado) : '');
    setArqueoDigital(data.cierre.digital_bs_contado != null ? String(data.cierre.digital_bs_contado) : '');
    setObsCierre(data.cierre.observacion || '');
    setEncargado(data.cierre.encargado || '');
    setRevisado(data.cierre.revisado || '');
    const bBs = {}, bUsd = {};
    (data.billetes || []).forEach((b) => { (b.moneda === 'USD' ? bUsd : bBs)[b.denominacion] = String(b.cantidad); });
    setBilletesBs(bBs);
    setBilletesUsd(bUsd);
    const pi = {};
    (data.formas || []).filter((f) => f.banco || f.lote).forEach((f) => { pi[f.id] = { banco: f.banco || '', lote: f.lote || '' }; });
    setPosInfo(pi);
  }

  // Acción rápida "Cerrar turno" del Inicio: abre el cierre apenas hay cajero
  // y turno (si hace falta, después del login del cajero). Una sola vez.
  const { state: navState } = useLocation();
  const pedirCierre = useRef(navState?.accion === 'cerrar_turno');
  useEffect(() => {
    if (!pedirCierre.current || offline || !estado?.cajero || !estado?.cierre) return;
    pedirCierre.current = false;
    abrirCierrePanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, offline]);

  async function abrirCierrePanel() {
    setErrCierre('');
    try {
      const { data } = await api.get(`/caja/cierre/${estado.cierre.id}/resumen`);
      hidratarCierre(data);
      setMostrarCierre(true);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar el resumen del turno.');
    }
  }

  const sumaBilletes = (mapa, denoms) => round2(denoms.reduce((s, d) => s + d * (Number(mapa[d]) || 0), 0));
  const contadoBsGrid = sumaBilletes(billetesBs, DENOM_BS);
  const contadoUsdGrid = sumaBilletes(billetesUsd, DENOM_USD);
  const usaGrid = contadoBsGrid > 0 || contadoUsdGrid > 0;
  const contadoBs = usaGrid ? contadoBsGrid : (Number(arqueoBs) || 0);
  const contadoUsd = usaGrid ? contadoUsdGrid : (Number(arqueoUsd) || 0);

  async function guardarArqueo() {
    setErrCierre('');
    setProcesandoCierre(true);
    try {
      const billetes = [
        ...DENOM_BS.map((d) => ({ moneda: 'BS', denominacion: d, cantidad: Number(billetesBs[d]) || 0 })),
        ...DENOM_USD.map((d) => ({ moneda: 'USD', denominacion: d, cantidad: Number(billetesUsd[d]) || 0 }))
      ].filter((b) => b.cantidad > 0);
      const pos = (resumen?.formas || [])
        .filter((f) => f.tipo === 'pos')
        .map((f) => ({ forma_pago_id: f.id, banco: posInfo[f.id]?.banco || '', lote: posInfo[f.id]?.lote || '' }));
      await api.post(`/caja/cierre/${estado.cierre.id}/arqueo`, {
        efectivo_bs_contado: contadoBs,
        efectivo_usd_contado: contadoUsd,
        digital_bs_contado: arqueoDigital === '' ? null : Number(arqueoDigital),
        observacion: obsCierre,
        encargado, revisado, billetes, pos
      });
      const { data } = await api.get(`/caja/cierre/${estado.cierre.id}/resumen`);
      hidratarCierre(data);
    } catch (err) {
      setErrCierre(err.response?.data?.error || 'No se pudo guardar el arqueo.');
    } finally {
      setProcesandoCierre(false);
    }
  }

  function descargarRechazados() {
    const blob = new Blob([JSON.stringify(rechazados, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `caja-tickets-rechazados-${empresaActiva}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function cerrarTurno() {
    if (!window.confirm('¿Cerrar el turno? No se podrán registrar ni anular más ventas de esta jornada.')) return;
    setErrCierre('');
    setProcesandoCierre(true);
    try {
      await api.post(`/caja/cierre/${estado.cierre.id}/cerrar`, { motivo_cierre_tardio: motivoTardio });
      setMotivoTardio('');
      setMostrarCierre(false);
      setResumen(null);
      await cargarTodo();
    } catch (err) {
      setErrCierre(err.response?.data?.error || 'No se pudo cerrar el turno.');
    } finally {
      setProcesandoCierre(false);
    }
  }

  const efBsEsperado = resumen ? Number(resumen.efectivo.bs_esperado) : 0;
  const efUsdEsperado = resumen ? Number(resumen.efectivo.usd_esperado) : 0;
  const difBs = round2(contadoBs - efBsEsperado);
  const difUsd = round2(contadoUsd - efUsdEsperado);
  const digEsperado = resumen?.digital ? Number(resumen.digital.bs_esperado) : 0;
  const usaDigital = !!resumen?.digital && (Number(resumen.digital.fondo_bs) > 0 || Number(resumen.digital.vueltos_bs) > 0 || arqueoDigital !== '');
  const difDigital = round2((Number(arqueoDigital) || 0) - digEsperado);
  const arqueoHecho = resumen?.cierre?.efectivo_bs_contado != null;

  // ---------------- Render ----------------
  if (cargando && !estado) {
    return <div className="p-8 text-center text-slate-400">Cargando caja…</div>;
  }

  const cabecera = (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <h1 className="text-2xl font-bold text-slate-800">
        Caja
        {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
        <BotonAyuda />
      </h1>
      <div className="flex items-center gap-3 text-sm text-slate-500">
        {(offline || cola.length > 0) && (
          <button
            onClick={() => flushCola()}
            title="Reintentar sincronización"
            className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${offline ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'}`}
          >
            {offline ? 'Sin conexión' : 'En línea'}{cola.length > 0 && ` · ${cola.length} sin sincronizar`}
          </button>
        )}
        {rechazados.length > 0 && (
          <button
            onClick={descargarRechazados}
            title="Descargar los tickets que el servidor no aceptó"
            className="text-xs px-2 py-1 rounded-full whitespace-nowrap bg-red-100 text-red-700"
          >
            {rechazados.length} rechazado{rechazados.length === 1 ? '' : 's'} · descargar
          </button>
        )}
        {estado?.tasa_bcv != null && <span>Tasa BCV <b className="text-slate-700">{formatMonto(estado.tasa_bcv, 4)}</b></span>}
        {estado?.cierre && <span>· Turno {estado.cierre.turno} del {formatFecha(estado.cierre.fecha)}</span>}
        {estado?.cajero && (
          <button onClick={cerrarSesionCajero} className="flex items-center gap-1 text-slate-400 hover:text-red-600" title="Cambiar cajero">
            <LogOut size={15} /> {estado.cajero.nombre}
          </button>
        )}
        {estado?.cajero && estado?.cierre && (
          <button
            onClick={abrirCierrePanel}
            title="Cierre y arqueo del turno"
            className="p-2 rounded-md text-white bg-slate-600 hover:bg-slate-700 transition-colors"
          >
            <ClipboardCheck size={16} />
          </button>
        )}
      </div>
    </div>
  );

  if (estado && estado.tasa_bcv == null) {
    return (
      <div className="space-y-6">
        {cabecera}
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-6 text-sm">
          No hay tasa BCV cargada para hoy. Cargala en{' '}
          <Link to="/tasas-cambio" className="underline font-medium">Tasas de Cambio</Link> para poder abrir la caja.
        </div>
      </div>
    );
  }

  if (estado && !estado.cajero) {
    return (
      <div className="space-y-6">
        {cabecera}
        <div className="bg-white rounded-xl shadow-sm p-6 max-w-sm mx-auto">
          <p className="font-medium text-slate-600 mb-4">Ingresá tu PIN</p>
          <form onSubmit={iniciarSesionCajero} className="space-y-3">
            <input
              required autoFocus type="password" inputMode="numeric" maxLength={4} placeholder="PIN (4 dígitos)"
              value={pin}
              onChange={(e) => {
                // Al completar el 4to dígito entra solo, sin presionar Enter.
                const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                setPin(v);
                if (v.length === 4) iniciarSesionCajero(null, v);
              }}
              className="border rounded-md px-3 py-2 w-full text-center tracking-[0.5em] text-lg"
            />
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <button type="submit" className="bg-primary-600 text-white rounded-md py-2 w-full hover:bg-primary-700">Entrar</button>
          </form>
          {cajeros.length === 0 && (
            <p className="text-xs text-slate-400 mt-3">No hay cajeros cargados. Pedile a un gerente que los cree en Caja · Configuración.</p>
          )}
        </div>
      </div>
    );
  }

  if (estado && !estado.cierre) {
    return (
      <div className="space-y-6">
        {cabecera}
        <div className="bg-white rounded-xl shadow-sm p-6 max-w-sm mx-auto">
          <p className="font-medium text-slate-600 mb-4">Abrir turno de caja</p>
          <form onSubmit={abrirTurno} className="space-y-3">
            <label className="block text-xs text-slate-500">
              Jornada
              <select value={turno} onChange={(e) => setTurno(e.target.value)} className="border rounded-md px-3 py-2 w-full mt-1">
                <option value="unico">Único</option>
                <option value="diurno">Diurno</option>
                <option value="nocturno">Nocturno</option>
              </select>
            </label>
            {puedeAbrirFechaPasada && (
              <label className="block text-xs text-slate-500">
                Fecha (para carga histórica de meses atrasados)
                <input
                  type="date" value={fechaApertura} max={hoyCaracas()}
                  onChange={(e) => setFechaApertura(e.target.value)}
                  className="border rounded-md px-3 py-2 w-full mt-1"
                />
              </label>
            )}
            <label className="block text-xs text-slate-500">
              Fondo inicial en efectivo Bs (para vueltos)
              <input
                type="number" step="0.01" min="0" value={fondoInicial}
                onChange={(e) => setFondoInicial(e.target.value)}
                className="border rounded-md px-3 py-2 w-full mt-1" placeholder="0,00"
              />
            </label>
            <label className="block text-xs text-slate-500">
              Fondo inicial en efectivo USD (para vueltos)
              <input
                type="number" step="0.01" min="0" value={fondoUsd}
                onChange={(e) => setFondoUsd(e.target.value)}
                className="border rounded-md px-3 py-2 w-full mt-1" placeholder="0,00"
              />
            </label>
            <label className="block text-xs text-slate-500">
              Saldo digital en Bs (para vueltos de pago móvil)
              <input
                type="number" step="0.01" min="0" value={fondoDigital}
                onChange={(e) => setFondoDigital(e.target.value)}
                className="border rounded-md px-3 py-2 w-full mt-1" placeholder="0,00"
              />
            </label>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <button type="submit" className="bg-primary-600 text-white rounded-md py-2 w-full hover:bg-primary-700">Abrir turno</button>
          </form>
        </div>
      </div>
    );
  }

  // ---- POS ----
  return (
    <>
      <div className="space-y-4 print:hidden">
        {cabecera}

        {aviso && (
          aviso.encolado ? (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm">
              Ticket guardado sin conexión (Total Bs {formatMonto(aviso.total_bs)}) — se sincroniza al reconectar.
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl px-4 py-3 text-sm">
              Ticket <b>#{aviso.numero}</b> cobrado — Total Bs {formatMonto(aviso.total_bs)}
              {aviso.vuelto_bs > 0 && <> · Vuelto Bs <b>{formatMonto(aviso.vuelto_bs)}</b></>}
              {aviso.propina_bs > 0 && <> · Propina Bs <b>{formatMonto(aviso.propina_bs)}</b></>}
              {aviso.factura_cxc_numero && <> · Factura CxC <b>#{aviso.factura_cxc_numero}</b></>}
            </div>
          )
        )}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>}

        {turnoVencido && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center space-y-3 max-w-lg mx-auto">
            <p className="font-semibold text-red-700">El turno del {formatFecha(estado.cierre.fecha)} ({estado.cierre.turno}) no se cerró a tiempo</p>
            <p className="text-sm text-red-700">
              No se puede seguir vendiendo hasta cerrarlo. Al cerrar hay que explicar el motivo; se avisa por correo a los supervisores.
              {!puedeCerrarTurno && ' Lo abrió otro usuario: lo cierra quien lo abrió o un contador, gerente o administrador; tú puedes dejar el arqueo guardado.'}
            </p>
            <button onClick={abrirCierrePanel} className="bg-red-600 hover:bg-red-700 text-white rounded-md px-4 py-2 text-sm font-semibold">
              {puedeCerrarTurno ? 'Cerrar el turno pendiente' : 'Hacer el arqueo del turno'}
            </button>
          </div>
        )}

        {!turnoVencido && (
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_40%] gap-4 items-start">
          {/* Columna izquierda: grupos arriba, productos o extras debajo */}
          <div className="space-y-4 min-w-0">
          {cobrando ? (
          /* Cobro en la zona izquierda: la factura sigue visible a la derecha */
          <div className="bg-white rounded-xl shadow-sm p-4">
            <p className="text-xs font-medium text-slate-500 mb-2">Cobro</p>
            <div className="bg-slate-50 rounded-lg p-3 text-sm mb-3 space-y-1">
              <div className="flex justify-between font-semibold text-slate-800 text-base">
                <span>Total</span><span>${formatMonto(totalUsd)} · Bs {formatMonto(totalUsd * tasa)}</span>
              </div>
              <div className="flex justify-between text-slate-500"><span>Pagado</span><span>${formatMonto(tendidoUsd)} · Bs {formatMonto(tendidoUsd * tasa)}</span></div>
              {faltaUsd > 0
                ? <div className="flex justify-between text-red-600 font-medium"><span>Falta</span><span>${formatMonto(faltaUsd)} · Bs {formatMonto(faltaUsd * tasa)}</span></div>
                : <div className="flex justify-between text-green-700 font-medium"><span>{propinaUsd > 0.005 ? 'Sobra' : 'Propina'}</span><span>${formatMonto(propinaUsd)} · Bs {formatMonto(propinaUsd * tasa)}</span></div>}
              {formaVuelto && vueltoUsdEf > 0.005 && (
                <>
                  <div className="flex justify-between text-slate-600"><span>Vuelto ({formaVuelto.nombre})</span><span>${formatMonto(vueltoUsdEf)} · Bs {formatMonto(vueltoUsdEf * tasa)}</span></div>
                  <div className="flex justify-between text-green-700"><span>Propina</span><span>${formatMonto(propinaFinalUsd)} · Bs {formatMonto(propinaFinalUsd * tasa)}</span></div>
                </>
              )}
            </div>

            {propinaUsd > 0.005 && formasVuelto.length > 0 && !usaCxc && (
              <div className="mb-3 rounded-lg border border-slate-200 p-3 text-sm">
                <p className="text-xs font-medium text-slate-500 mb-2">¿Dar vuelto? (si no, todo queda como propina)</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button" onClick={() => { setVueltoFormaId(null); setVueltoMonto(''); }}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium ${!formaVuelto ? 'border-2 border-primary-600 bg-primary-50 text-primary-700' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  >Todo propina</button>
                  {formasVuelto.map((f) => (
                    <button
                      key={f.id} type="button" onClick={() => { setVueltoFormaId(f.id); setVueltoMonto(''); }}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium ${formaVuelto?.id === f.id ? 'border-2 border-primary-600 bg-primary-50 text-primary-700' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                    >Vuelto en {f.nombre}</button>
                  ))}
                </div>
                {formaVuelto && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-slate-500 text-xs flex-1">Monto del vuelto ({formaVuelto.moneda === 'USD' ? '$' : 'Bs'})</span>
                    <input
                      type="number" step="0.01" min="0" inputMode="decimal"
                      value={vueltoMonto !== '' ? vueltoMonto : String(excedenteEnForma)}
                      onChange={(e) => setVueltoMonto(e.target.value.replace('-', ''))}
                      className="border rounded-md px-2 py-1.5 w-28 text-right"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Formas de pago como botones */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {formasPago.map((f) => {
                const elegida = formasElegidas.includes(f.id);
                const sinConexion = f.tipo === 'cuenta_por_cobrar' && offline;
                return (
                  <button
                    key={f.id} type="button" onClick={() => !sinConexion && elegirForma(f)}
                    disabled={sinConexion}
                    title={sinConexion ? 'Cuenta por Cobrar necesita conexión' : undefined}
                    className={`rounded-lg p-3 text-sm font-medium transition-colors ${sinConexion ? 'border border-slate-200 text-slate-300 cursor-not-allowed' : elegida ? 'border-2 border-primary-600 bg-primary-50 text-primary-700' : 'border border-slate-200 text-slate-600 hover:border-primary-400 hover:bg-primary-50'}`}
                  >
                    {f.nombre}
                    <span className="block text-[11px] font-normal text-slate-400">{f.moneda === 'USD' ? '$' : 'Bs'}</span>
                  </button>
                );
              })}
              {formasPago.length === 0 && <p className="col-span-full text-xs text-slate-400">No hay formas de pago activas. Cargalas en Caja · Configuración.</p>}
            </div>

            {/* Cuenta por Cobrar: a qué cliente se le carga el ticket */}
            {usaCxc && (
              <div className="mt-3 rounded-lg border border-slate-200 p-3 text-sm">
                <p className="text-xs font-medium text-slate-500 mb-2">¿A qué cliente se le carga esta cuenta?</p>
                <AutocompleteProducto
                  productos={clientesCxc}
                  value={clienteCxcId}
                  onChange={setClienteCxcId}
                  onCrear={abrirNuevoClienteCxc}
                  placeholder="Buscar cliente..."
                />
                {errorClienteCxc && !nuevoClienteCxc && <p className="text-red-600 text-xs mt-1">{errorClienteCxc}</p>}
              </div>
            )}

            {nuevoClienteCxc && (
              <Modal title="Nuevo cliente" onClose={() => setNuevoClienteCxc(null)}>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Nombre</label>
                    <input
                      value={nuevoClienteCxc.nombre}
                      onChange={(e) => setNuevoClienteCxc((d) => ({ ...d, nombre: e.target.value }))}
                      className="border rounded-md px-2 py-1.5 w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Teléfono (opcional)</label>
                    <input
                      value={nuevoClienteCxc.telefono}
                      onChange={(e) => setNuevoClienteCxc((d) => ({ ...d, telefono: e.target.value }))}
                      className="border rounded-md px-2 py-1.5 w-full text-sm"
                    />
                  </div>
                  {errorClienteCxc && <p className="text-red-600 text-xs">{errorClienteCxc}</p>}
                  <div className="flex gap-2 justify-end pt-1">
                    <button onClick={() => setNuevoClienteCxc(null)} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600">Cancelar</button>
                    <button
                      onClick={crearClienteCxc} disabled={guardandoClienteCxc || !nuevoClienteCxc.nombre.trim()}
                      className="rounded-md bg-primary-600 text-white px-3 py-1.5 text-sm font-medium disabled:opacity-40"
                    >
                      {guardandoClienteCxc ? 'Creando…' : 'Crear cliente'}
                    </button>
                  </div>
                </div>
              </Modal>
            )}

            {/* Montos de las formas elegidas */}
            {formasElegidas.length > 0 && (
              <div className="mt-3 space-y-2">
                {formasElegidas.map((id) => {
                  const f = formasPago.find((x) => x.id === id);
                  if (!f) return null;
                  const faltaForma = f.moneda === 'USD' ? faltaUsd : round2(faltaUsd * tasa);
                  return (
                    <div key={id} className="flex items-center gap-2">
                      <span className="flex-1 text-sm text-slate-700 truncate">{f.nombre}</span>
                      <button
                        type="button" onClick={() => completarConFalta(f)} disabled={faltaUsd <= 0}
                        className="rounded-md border border-primary-300 bg-primary-50 text-primary-700 px-2.5 py-1.5 text-xs font-medium hover:bg-primary-100 disabled:opacity-40"
                        title="Completar con lo que falta"
                      >
                        = Falta {f.moneda === 'USD' ? '$' : 'Bs'} {formatMonto(faltaForma)}
                      </button>
                      <input
                        type="number" step="0.01" min="0" inputMode="decimal" value={pagos[f.id] || ''}
                        onChange={(e) => setPagos((prev) => ({ ...prev, [f.id]: e.target.value.replace('-', '') }))}
                        className="border rounded-md px-2 py-1.5 w-28 text-right" placeholder="0,00"
                      />
                      <button type="button" onClick={() => quitarForma(f.id)} title="Quitar" className="p-1 text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
                    </div>
                  );
                })}
              </div>
            )}

            {errorCobro && <p className="text-red-600 text-sm mt-2">{errorCobro}</p>}

            <div className="mt-4 flex gap-2">
              <button onClick={() => setCobrando(false)} className="flex items-center gap-1 rounded-lg border border-slate-300 px-4 py-3 text-sm text-slate-600 hover:bg-slate-50">
                <ArrowLeft size={16} /> Volver a productos
              </button>
              <button onClick={confirmarCobro} disabled={guardando || faltaUsd > 0.01 || (usaCxc && !clienteCxcId)} className="flex-1 rounded-lg bg-green-600 text-white py-3 font-semibold hover:bg-green-700 disabled:opacity-40">
                {guardando ? 'Registrando…' : 'Confirmar cobro'}
              </button>
            </div>
          </div>
          ) : (<>
          {/* Grupos */}
          <div className="bg-white rounded-xl shadow-sm p-3">
            <div className="flex items-center gap-2 mb-2">
              {grupoSel !== 0 && (
                <button onClick={() => { setPizzaActiva(null); setGrupoSel(grupoActual?.padre_id || 0); }} className="p-1 rounded-md text-slate-400 hover:text-primary-600" title="Volver">
                  <ArrowLeft size={16} />
                </button>
              )}
              <span className="text-xs font-medium text-slate-500">{grupoActual ? grupoActual.nombre : 'Grupos'}</span>
              {puedeEditarPantalla && (
                <button
                  onClick={() => setEditandoPantalla((v) => !v)}
                  title={editandoPantalla ? 'Terminar edición' : 'Editar pantalla (íconos de grupos)'}
                  className={`ml-auto p-1.5 rounded-md transition-colors ${editandoPantalla ? 'bg-primary-600 text-white hover:bg-primary-700' : 'text-slate-400 hover:text-primary-600'}`}
                >
                  {editandoPantalla ? <Check size={15} /> : <Pencil size={15} />}
                </button>
              )}
            </div>
            {editandoPantalla && (
              <p className="text-[11px] text-primary-700 bg-primary-50 rounded-md px-2 py-1 mb-2">
                Editando: tocá el lápiz de una tarjeta para cambiar su ícono.
              </p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {gruposNivel.map((g) => (
                <div key={g.id} className="relative">
                  <button
                    onClick={() => { setPizzaActiva(null); setGrupoSel(g.id); }}
                    className={`w-full h-full flex flex-col items-center justify-center gap-1 rounded-lg border p-3 text-center hover:border-primary-400 hover:bg-primary-50 transition-colors ${editandoPantalla ? 'border-dashed border-primary-300' : 'border-slate-200'}`}
                  >
                    <IconoGrupo nombre={g.icono} size={24} className="text-primary-600" />
                    <span className="text-xs font-medium text-slate-600 leading-tight">{g.nombre}</span>
                  </button>
                  {editandoPantalla && (
                    <button
                      onClick={() => setGrupoIcono(g)}
                      title="Cambiar ícono"
                      className="absolute top-1 right-1 p-1 rounded-md bg-white shadow text-slate-500 hover:text-primary-600"
                    >
                      <Pencil size={12} />
                    </button>
                  )}
                </div>
              ))}
              {gruposNivel.length === 0 && grupoSel === 0 && (
                <p className="col-span-full text-sm text-slate-400 p-4 text-center">No hay grupos de productos.</p>
              )}
            </div>
          </div>

          {/* Extras de la pizza recién agregada: reemplazan a los productos */}
          {extrasAbiertos ? (
          <div className="bg-white rounded-xl shadow-sm p-3">
            <div className="mb-2">
              <span className="text-xs font-medium text-slate-500">Extras para <b className="text-slate-700">{pizzaSel.nombre}</b></span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <button
                onClick={terminarExtras}
                className="flex flex-col items-center justify-center rounded-lg border-2 border-slate-300 p-2 min-h-[4rem] text-center hover:border-primary-400 hover:bg-primary-50 transition-colors"
              >
                <span className="text-sm font-semibold text-slate-700">Sin extra</span>
              </button>
              {extrasDisponibles.map((x) => {
                const puesto = ticket.some((l) => l.padre === pizzaSel.key && l.producto_id === x.id);
                return (
                  <button
                    key={x.id} onClick={() => alternarExtra(x)} disabled={x.precio_usd == null}
                    className={`relative flex flex-col rounded-lg border p-2 text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${puesto ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500' : 'border-slate-200 hover:border-primary-400 hover:bg-primary-50'}`}
                  >
                    {puesto && <Check size={14} className="absolute top-1.5 right-1.5 text-amber-600" />}
                    <span className="text-xs font-medium text-slate-700 leading-tight min-h-[2rem] pr-4">{x.nombre}</span>
                    {x.precio_usd == null
                      ? <span className="text-[11px] text-red-500 mt-1">sin precio</span>
                      : <span className="text-[11px] text-slate-500 mt-1">${formatMonto(x.precio_usd)} · Bs {formatMonto(x.precio_usd * tasa)}</span>}
                  </button>
                );
              })}
            </div>
          </div>
          ) : productosNivel.length > 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-3">
            <span className="text-xs font-medium text-slate-500">Productos</span>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {productosNivel.map((p) => (
                <button
                  key={p.id} onClick={() => agregar(p)} disabled={p.precio_usd == null}
                  className="flex flex-col rounded-lg border border-slate-200 p-2 text-left hover:border-primary-400 hover:bg-primary-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <span className="text-xs font-medium text-slate-700 leading-tight min-h-[2rem]">{p.nombre}</span>
                  {p.precio_usd == null
                    ? <span className="text-[11px] text-red-500 mt-1">sin precio</span>
                    : <span className="text-[11px] text-slate-500 mt-1">${formatMonto(p.precio_usd)} · Bs {formatMonto(p.precio_usd * tasa)}</span>}
                </button>
              ))}
            </div>
          </div>
          ) : null}
          </>)}
          </div>

          {/* Ticket */}
          <div className="bg-white rounded-xl shadow-sm p-3 md:sticky md:top-4">
            {/* Canal como tarjetas (igual que los grupos); "Listo" se suma mientras hay extras abiertos */}
            <div className={`grid gap-2 mb-3 ${extrasAbiertos ? 'grid-cols-3' : 'grid-cols-2'}`}>
              {extrasAbiertos && (
                <button onClick={terminarExtras} className="flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-green-600 bg-green-600 hover:bg-green-700 p-3 text-white transition-colors">
                  <Check size={24} />
                  <span className="text-xs font-semibold">Listo</span>
                </button>
              )}
              {[
                { valor: 'mostrador', label: 'Mostrador', Icono: Store },
                { valor: 'delivery', label: 'Delivery', Icono: Bike }
              ].map(({ valor, label, Icono }) => (
                <button
                  key={valor}
                  onClick={() => {
                    if (valor === 'delivery') { setEligiendoPlataforma(true); return; }
                    setCanal('mostrador');
                    setPlataformaId('');
                  }}
                  className={`flex flex-col items-center justify-center gap-1 rounded-lg p-3 transition-colors ${canal === valor ? 'border-2 border-primary-600 bg-primary-50 text-primary-700' : 'border border-slate-200 text-slate-500 hover:border-primary-400 hover:bg-primary-50'}`}
                >
                  <Icono size={24} className={canal === valor ? 'text-primary-600' : 'text-slate-400'} />
                  <span className={`text-xs ${canal === valor ? 'font-semibold' : 'font-medium'}`}>{label}</span>
                  {valor === 'delivery' && canal === 'delivery' && (
                    <span className="text-[11px] leading-tight text-primary-600">
                      {(catalogo?.plataformas || []).find((pl) => String(pl.id) === String(plataformaId))?.nombre || 'Sin definir'}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="divide-y max-h-[40vh] overflow-y-auto">
              {ticket.map((l) => (l.padre ? (
                <div key={l.key} className="py-1 pl-5 flex items-center gap-2 text-xs border-t-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-600 truncate">+ {l.nombre}</p>
                    <p className="text-slate-400">${formatMonto(l.precio_usd)} × {l.cantidad}</p>
                  </div>
                  <button onClick={() => quitar(l.key)} title="Quitar extra" className="p-1 rounded text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                </div>
              ) : (
                <div key={l.key} className={`py-2 flex items-center gap-2 text-sm ${l.key === pizzaActiva ? 'bg-amber-50 -mx-1 px-1 rounded' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-700 truncate">{l.nombre}</p>
                    <p className="text-xs text-slate-400">${formatMonto(l.precio_usd)} · Bs {formatMonto(l.precio_usd * tasa)}</p>
                  </div>
                  {l.tieneExtras && (
                    <button onClick={() => setPizzaActiva(l.key)} title="Agregar extras" className="p-1 rounded text-amber-500 hover:text-amber-700"><Plus size={14} className="stroke-[3]" /></button>
                  )}
                  <button onClick={() => cambiarCantidad(l.key, -1)} className="p-1 rounded text-slate-400 hover:text-primary-600"><Minus size={14} /></button>
                  <span className="w-6 text-center tabular-nums">{l.cantidad}</span>
                  <button onClick={() => cambiarCantidad(l.key, 1)} className="p-1 rounded text-slate-400 hover:text-primary-600"><Plus size={14} /></button>
                  <button onClick={() => quitar(l.key)} className="p-1 rounded text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              )))}
              {ticket.length === 0 && <p className="py-6 text-center text-sm text-slate-400">Ticket vacío</p>}
            </div>

            <div className="border-t mt-2 pt-2 text-sm space-y-1">
              <Fila etiqueta="Base" usd={baseUsd} bs={baseUsd * tasa} />
              <div className="flex items-center justify-between gap-2">
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs" role="group" aria-label="IVA del ticket">
                  <button type="button" onClick={() => setConIva(true)} className={`px-2.5 py-1 ${conIva ? 'bg-primary-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>Con IVA</button>
                  <button type="button" onClick={() => setConIva(false)} className={`px-2.5 py-1 ${!conIva ? 'bg-primary-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>Sin IVA</button>
                </div>
                <span className="text-slate-500">{conIva ? `IVA ${formatMonto(ivaPct, 2)}%` : 'Sin IVA (solo base)'} · ${formatMonto(ivaUsd)} · Bs {formatMonto(ivaUsd * tasa)}</span>
              </div>
              <div className="flex justify-between font-semibold text-slate-800 pt-1">
                <span>Total</span>
                <span>${formatMonto(totalUsd)} · Bs {formatMonto(totalUsd * tasa)}</span>
              </div>
            </div>

            <div className="flex gap-2 mt-3">
              <button onClick={abrirItemLibre} className="rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-500 hover:bg-slate-50" title="Agregar ítem que no está en el catálogo">Ítem libre</button>
              <button onClick={nuevoTicket} disabled={ticket.length === 0} className="rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 disabled:opacity-40">Vaciar</button>
              <button onClick={abrirCobro} disabled={ticket.length === 0 || cobrando} className="flex-1 rounded-md bg-green-600 text-white py-2 text-sm font-semibold hover:bg-green-700 disabled:opacity-40">Cobrar</button>
            </div>
          </div>
        </div>
        )}

        {eligiendoPlataforma && (
          <Modal title="Delivery · plataforma" onClose={() => setEligiendoPlataforma(false)} maxWidth="max-w-md">
            <div className="grid grid-cols-2 gap-2">
              {[{ id: '', nombre: 'Sin definir' }, ...(catalogo?.plataformas || [])].map((pl) => {
                const activa = canal === 'delivery' && String(plataformaId) === String(pl.id);
                return (
                  <button
                    key={pl.id || 'sin'}
                    onClick={() => { setCanal('delivery'); setPlataformaId(String(pl.id)); setEligiendoPlataforma(false); }}
                    className={`rounded-lg p-4 text-sm font-medium transition-colors ${activa ? 'border-2 border-primary-600 bg-primary-50 text-primary-700' : pl.id ? 'border border-slate-200 text-slate-700 hover:border-primary-400 hover:bg-primary-50' : 'border border-dashed border-slate-300 text-slate-500 hover:border-primary-400 hover:bg-primary-50'}`}
                  >
                    {pl.nombre}
                  </button>
                );
              })}
            </div>
          </Modal>
        )}

        {grupoIcono && (
          <Modal title={`Ícono de ${grupoIcono.nombre}`} onClose={() => setGrupoIcono(null)} maxWidth="max-w-lg">
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {ICONOS_GRUPO.map((i) => {
                const actual = (grupoIcono.icono || 'utensils') === i.value;
                return (
                  <button
                    key={i.value} onClick={() => asignarIcono(grupoIcono, i.value)} title={i.label}
                    className={`flex flex-col items-center gap-1 rounded-lg border p-2 transition-colors ${actual ? 'border-primary-500 bg-primary-50' : 'border-slate-200 hover:border-primary-400 hover:bg-primary-50'}`}
                  >
                    <i.Comp size={22} className="text-primary-600" />
                    <span className="text-[10px] text-slate-500 leading-tight text-center">{i.label}</span>
                  </button>
                );
              })}
            </div>
          </Modal>
        )}

        {itemLibre && (
          <Modal title="Ítem libre" onClose={() => setItemLibre(false)} maxWidth="max-w-sm">
            <form onSubmit={agregarItemLibre} className="space-y-3">
              <input
                autoFocus required placeholder="Descripción"
                value={ilDesc} onChange={(e) => setIlDesc(e.target.value)}
                className="border rounded-md px-3 py-2 w-full"
              />
              <div className="flex gap-2">
                <label className="flex-1 text-xs text-slate-500">
                  Precio unitario ($)
                  <input type="number" step="0.01" min="0.01" required value={ilPrecio} onChange={(e) => setIlPrecio(e.target.value.replace('-', ''))} className="border rounded-md px-3 py-2 w-full mt-1" placeholder="0,00" />
                </label>
                <label className="w-24 text-xs text-slate-500">
                  Cantidad
                  <input type="number" step="1" min="1" required value={ilCant} onChange={(e) => setIlCant(e.target.value.replace('-', ''))} className="border rounded-md px-3 py-2 w-full mt-1" />
                </label>
              </div>
              <button type="submit" className="bg-primary-600 text-white rounded-md py-2 w-full hover:bg-primary-700">Agregar</button>
            </form>
          </Modal>
        )}


        {mostrarCierre && resumen && (
          <Modal title="Cierre y arqueo del turno" onClose={() => setMostrarCierre(false)} maxWidth="max-w-2xl">
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <Dato etiqueta="Tickets cobrados" valor={resumen.ventas.tickets} />
                <Dato etiqueta="Anuladas" valor={resumen.ventas.anuladas} />
                <Dato etiqueta="Base" valor={`$${formatMonto(resumen.ventas.base_usd)} · Bs ${formatMonto(resumen.ventas.base_bs)}`} />
                <Dato etiqueta={`IVA`} valor={`$${formatMonto(resumen.ventas.iva_usd)} · Bs ${formatMonto(resumen.ventas.iva_bs)}`} />
                <Dato etiqueta="Total vendido" valor={`$${formatMonto(resumen.ventas.total_usd)} · Bs ${formatMonto(resumen.ventas.total_bs)}`} />
                <Dato etiqueta="Propinas" valor={`$${formatMonto(resumen.ventas.propina_usd)} · Bs ${formatMonto(resumen.ventas.propina_bs)}`} />
                {resumen.fiscal && (
                  <Dato etiqueta={`Fiscal · IVA declarado $${formatMonto(resumen.fiscal.iva_usd)}`} valor={`$${formatMonto(resumen.fiscal.total_usd)} · Bs ${formatMonto(resumen.fiscal.total_bs)}`} />
                )}
                {resumen.paralelo && (
                  <Dato etiqueta="Paralelo · venta (IVA no se declara)" valor={`$${formatMonto(resumen.paralelo.venta_usd)} · Bs ${formatMonto(resumen.paralelo.venta_bs)}`} />
                )}
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr><th className="p-2">Forma de pago</th><th className="p-2 text-right">Bs</th><th className="p-2 text-right">$</th></tr>
                  </thead>
                  <tbody>
                    {resumen.formas.map((f) => (
                      <tr key={f.id} className="border-t">
                        <td className="p-2">{f.nombre} <span className="text-xs text-slate-400">({TIPO_FORMA_LABEL[f.tipo] || f.tipo})</span>{f.fiscal === false && <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded-full bg-slate-800 text-white">Paralelo</span>}</td>
                        <td className="p-2 text-right whitespace-nowrap">{formatMonto(f.total_bs)}</td>
                        <td className="p-2 text-right whitespace-nowrap">{formatMonto(f.total_usd)}</td>
                      </tr>
                    ))}
                    {resumen.formas.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-slate-400">Sin cobros en el turno.</td></tr>}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="text-xs text-slate-500">Encargado
                  <input value={encargado} onChange={(e) => setEncargado(e.target.value)} className="border rounded-md px-2 py-1 w-full mt-1" />
                </label>
                <label className="text-xs text-slate-500">Revisado
                  <input value={revisado} onChange={(e) => setRevisado(e.target.value)} className="border rounded-md px-2 py-1 w-full mt-1" />
                </label>
              </div>

              {resumen.formas.some((f) => f.tipo === 'pos') && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Lote de POS</p>
                  <div className="space-y-1">
                    {resumen.formas.filter((f) => f.tipo === 'pos').map((f) => (
                      <div key={f.id} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                        <span className="text-slate-600 truncate">{f.nombre}</span>
                        <input
                          placeholder="banco" value={posInfo[f.id]?.banco || ''}
                          onChange={(e) => setPosInfo((p) => ({ ...p, [f.id]: { ...p[f.id], banco: e.target.value } }))}
                          className="border rounded-md px-2 py-1 w-28"
                        />
                        <input
                          placeholder="lote / nº" value={posInfo[f.id]?.lote || ''}
                          onChange={(e) => setPosInfo((p) => ({ ...p, [f.id]: { ...p[f.id], lote: e.target.value } }))}
                          className="border rounded-md px-2 py-1 w-32"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Arqueo de efectivo — por billete</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <GrillaBilletes titulo="Moneda nacional (Bs)" simbolo="Bs" denoms={DENOM_BS} mapa={billetesBs} setMapa={setBilletesBs} total={contadoBsGrid} />
                  <GrillaBilletes titulo="Moneda extranjera ($)" simbolo="$" denoms={DENOM_USD} mapa={billetesUsd} setMapa={setBilletesUsd} total={contadoUsdGrid} />
                </div>
                {!usaGrid && (
                  <div className="grid grid-cols-[1fr_auto] gap-2 items-center mt-2">
                    <span className="text-slate-500 text-xs">O total directo — Bs contado</span>
                    <input type="number" step="0.01" min="0" value={arqueoBs} onChange={(e) => setArqueoBs(e.target.value)} className="border rounded-md px-2 py-1 w-32 text-right" placeholder="0,00" />
                    <span className="text-slate-500 text-xs">$ contado</span>
                    <input type="number" step="0.01" min="0" value={arqueoUsd} onChange={(e) => setArqueoUsd(e.target.value)} className="border rounded-md px-2 py-1 w-32 text-right" placeholder="0,00" />
                  </div>
                )}
                <div className="mt-2 text-xs space-y-0.5">
                  <div>Bs · esperado {formatMonto(efBsEsperado)} · contado {formatMonto(contadoBs)} · <b className={difBs === 0 ? 'text-slate-500' : difBs < 0 ? 'text-red-600' : 'text-green-700'}>{difBs > 0 ? '+' : ''}{formatMonto(difBs)}</b></div>
                  <div>$ · esperado {formatMonto(efUsdEsperado)} · contado {formatMonto(contadoUsd)} · <b className={difUsd === 0 ? 'text-slate-500' : difUsd < 0 ? 'text-red-600' : 'text-green-700'}>{difUsd > 0 ? '+' : ''}{formatMonto(difUsd)}</b></div>
                  {usaDigital && (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span>Saldo digital Bs · fondo {formatMonto(resumen.digital.fondo_bs)} − vueltos {formatMonto(resumen.digital.vueltos_bs)} · esperado {formatMonto(digEsperado)}</span>
                      <input
                        type="number" step="0.01" min="0" value={arqueoDigital} onChange={(e) => setArqueoDigital(e.target.value)}
                        className="border rounded-md px-2 py-1 w-28 text-right" placeholder="Saldo restante"
                      />
                      {arqueoDigital !== '' && <b className={difDigital === 0 ? 'text-slate-500' : difDigital < 0 ? 'text-red-600' : 'text-green-700'}>{difDigital > 0 ? '+' : ''}{formatMonto(difDigital)}</b>}
                    </div>
                  )}
                </div>
                <textarea
                  value={obsCierre} onChange={(e) => setObsCierre(e.target.value)}
                  placeholder="Observación (opcional)" rows={2}
                  className="border rounded-md px-3 py-2 w-full mt-2 text-sm"
                />
                {turnoVencido && (
                  <>
                    <textarea
                      value={motivoTardio} onChange={(e) => setMotivoTardio(e.target.value)}
                      placeholder="Motivo del cierre tardío (obligatorio) — se envía a los supervisores" rows={2}
                      className="border border-red-300 rounded-md px-3 py-2 w-full mt-2 text-sm"
                    />
                    <p className="text-xs text-red-600 mt-0.5">
                      Mínimo 10 caracteres para habilitar "Cerrar turno" ({motivoTardio.trim().length}/10)
                    </p>
                  </>
                )}
              </div>

              {errCierre && <p className="text-red-600 text-sm">{errCierre}</p>}

              <div className="flex flex-wrap gap-2 justify-end">
                <button onClick={() => window.print()} title="Imprimir reporte Z" className="p-2 rounded-md text-white bg-slate-600 hover:bg-slate-700">
                  <Printer size={16} />
                </button>
                <button onClick={guardarArqueo} disabled={procesandoCierre} className="rounded-md border border-slate-300 px-3 py-2 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
                  Guardar arqueo
                </button>
                {puedeCerrarTurno && (
                  <button onClick={cerrarTurno} disabled={procesandoCierre || !arqueoHecho || (turnoVencido && motivoTardio.trim().length < 10)} className="rounded-md bg-red-600 text-white px-3 py-2 font-semibold hover:bg-red-700 disabled:opacity-40">
                    Cerrar turno
                  </button>
                )}
              </div>
              {puedeCerrarTurno
                ? !arqueoHecho && <p className="text-xs text-slate-400 text-right">Guardá el arqueo antes de cerrar.</p>
                : <p className="text-xs text-slate-500 text-right">Guarda el arqueo; este turno lo abrió otro usuario, así que lo cierra quien lo abrió o un contador, gerente o administrador.</p>}
            </div>
          </Modal>
        )}
      </div>

      {/* Reporte Z para impresión (blanco y negro, carta — @media print global) */}
      {mostrarCierre && resumen && (
        <div className="hidden print:block">
          <h2 style={{ fontFamily: 'Georgia, "Times New Roman", serif' }} className="text-lg font-bold">{empresaActivaNombre}</h2>
          <p className="text-xs">Reporte Z · Cierre de caja</p>
          <p className="text-xs">
            Turno {resumen.cierre.turno} · {formatFecha(resumen.cierre.fecha)} · Tasa BCV {formatMonto(resumen.cierre.tasa_bcv, 4)} · IVA {formatMonto(resumen.cierre.iva_pct, 2)}%
          </p>
          <table className="w-full text-xs mt-3">
            <tbody>
              <tr><td>Tickets cobrados</td><td className="text-right">{resumen.ventas.tickets}</td></tr>
              <tr><td>Anuladas</td><td className="text-right">{resumen.ventas.anuladas}</td></tr>
              <tr><td>Base</td><td className="text-right">$ {formatMonto(resumen.ventas.base_usd)} / Bs {formatMonto(resumen.ventas.base_bs)}</td></tr>
              <tr><td>IVA</td><td className="text-right">$ {formatMonto(resumen.ventas.iva_usd)} / Bs {formatMonto(resumen.ventas.iva_bs)}</td></tr>
              <tr><td>Total vendido</td><td className="text-right">$ {formatMonto(resumen.ventas.total_usd)} / Bs {formatMonto(resumen.ventas.total_bs)}</td></tr>
              <tr><td>Propinas</td><td className="text-right">$ {formatMonto(resumen.ventas.propina_usd)} / Bs {formatMonto(resumen.ventas.propina_bs)}</td></tr>
            </tbody>
          </table>
          <table className="w-full text-xs mt-3">
            <thead><tr><th className="text-left">Forma de pago</th><th className="text-right">Bs</th><th className="text-right">$</th></tr></thead>
            <tbody>
              {resumen.formas.map((f) => (
                <tr key={f.id}><td>{f.nombre}</td><td className="text-right">{formatMonto(f.total_bs)}</td><td className="text-right">{formatMonto(f.total_usd)}</td></tr>
              ))}
            </tbody>
          </table>
          <table className="w-full text-xs mt-3">
            <tbody>
              <tr><td>Efectivo Bs — esperado</td><td className="text-right">{formatMonto(efBsEsperado)}</td></tr>
              <tr><td>Efectivo Bs — contado</td><td className="text-right">{formatMonto(contadoBs)}</td></tr>
              <tr><td>Diferencia Bs</td><td className="text-right">{formatMonto(difBs)}</td></tr>
              <tr><td>Efectivo $ — esperado</td><td className="text-right">{formatMonto(efUsdEsperado)}</td></tr>
              <tr><td>Efectivo $ — contado</td><td className="text-right">{formatMonto(contadoUsd)}</td></tr>
              <tr><td>Diferencia $</td><td className="text-right">{formatMonto(difUsd)}</td></tr>
            </tbody>
          </table>
          {obsCierre && <p className="text-xs mt-3">Obs.: {obsCierre}</p>}
        </div>
      )}
    </>
  );
}

function Fila({ etiqueta, usd, bs }) {
  return (
    <div className="flex justify-between text-slate-500">
      <span>{etiqueta}</span>
      <span>${formatMonto(usd)} · Bs {formatMonto(bs)}</span>
    </div>
  );
}

function Dato({ etiqueta, valor }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{etiqueta}</p>
      <p className="text-slate-700">{valor}</p>
    </div>
  );
}

function GrillaBilletes({ titulo, simbolo, denoms, mapa, setMapa, total }) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="bg-slate-50 px-2 py-1 text-xs font-medium text-slate-500">{titulo}</div>
      <table className="w-full text-xs">
        <tbody>
          {denoms.map((d) => (
            <tr key={d} className="border-t">
              <td className="px-2 py-1 whitespace-nowrap">{simbolo} {formatMonto(d)}</td>
              <td className="px-2 py-1">
                <input
                  type="number" min="0" step="1" value={mapa[d] || ''}
                  onChange={(e) => setMapa((m) => ({ ...m, [d]: e.target.value }))}
                  className="border rounded px-1 py-0.5 w-16 text-right"
                />
              </td>
              <td className="px-2 py-1 text-right whitespace-nowrap text-slate-500">{formatMonto(d * (Number(mapa[d]) || 0))}</td>
            </tr>
          ))}
          <tr className="border-t bg-slate-50">
            <td className="px-2 py-1 font-medium" colSpan={2}>Total</td>
            <td className="px-2 py-1 text-right font-medium whitespace-nowrap">{formatMonto(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
